import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  tSharedAppDispatch,
  tSharedAppState,
  lessonApi,
  LessonApiTypes,
  ILessonShow,
  ILessonFeedback
} from 'common/modules/ReactShared';
import { AxiosError } from 'axios';
import { isPreviewingLesson } from '../preview';

type tLessonData = ILessonShow | null;
const initialLessonState: tLessonData = null;

const fetchLesson = createAsyncThunk<
  LessonApiTypes.ILessonGetResponse['lesson'],
  ILessonShow['id'],
  { state: tSharedAppState }
>('lesson/fetchLesson_request', async (lessonId, { getState }) => {
  const { lesson } = await lessonApi.fetchLesson(
    lessonId,
    isPreviewingLesson(getState(), lessonId)
  );
  return lesson;
});

const followTeam = createAsyncThunk<void, number, { state: tSharedAppState }>(
  'lesson/followTeam_request',
  async (teamId) => {
    await lessonApi.followTeam(teamId);
  }
);

const unfollowTeam = createAsyncThunk<void, number, { state: tSharedAppState }>(
  'lesson/unfollowTeam_request',
  async (teamId) => {
    await lessonApi.unfollowTeam(teamId);
  }
);

const toggleFollowTeam = createAsyncThunk<
  void,
  { teamId: number; follow: boolean },
  { dispatch: tSharedAppDispatch }
>('lesson/toggleFollowTeam', async ({ teamId, follow }, { dispatch }) => {
  if (follow) {
    dispatch(followTeam(teamId));
    dispatch(actions.updateFollowersNumber('inc'));
  } else {
    dispatch(unfollowTeam(teamId));
    dispatch(actions.updateFollowersNumber('dec'));
  }
});

const getLessonRating = createAsyncThunk<ILessonFeedback, { lessonId: number }>(
  'lesson/getRating_request',
  async ({ lessonId }, { rejectWithValue }) => {
    try {
      return await lessonApi.fetchLessonRating(lessonId);
    } catch (err) {
      return rejectWithValue((err as AxiosError).message);
    }
  }
);

const submitLessonFeedback = createAsyncThunk<
  ILessonFeedback,
  { lessonId: number; body: ILessonFeedback; update?: boolean }
>(
  'lesson/submitLessonFeedback_request',
  async ({ lessonId, body, update }, { rejectWithValue }) => {
    try {
      if (!update) {
        return await lessonApi.submitLessonFeedback(lessonId, body);
      }
      return await lessonApi.updateLessonFeedback(lessonId, body);
    } catch (err) {
      return rejectWithValue((err as AxiosError).message);
    }
  }
);

const lessonSlice = createSlice({
  name: 'lesson',
  initialState: initialLessonState as tLessonData,
  reducers: {
    drop: () => initialLessonState,
    updateFollowersNumber: (state, action: PayloadAction<'inc' | 'dec'>) => {
      if (state?.team) {
        switch (action.payload) {
          case 'inc':
            state.team.followersCount += 1;
            state.team.isFollowing = true;
            break;
          case 'dec':
            state.team.followersCount -= 1;
            state.team.isFollowing = false;
            break;
          default:
            break;
        }
      }
    }
  },

  extraReducers: (builder) => {
    builder.addCase(fetchLesson.pending, () => {
      return initialLessonState;
    });

    builder.addCase(fetchLesson.fulfilled, (_, action) => {
      return {
        ...action.payload,
        level: action.payload.level === 'zero_level' ? 'a1' : action.payload.level
      };
    });

    builder.addCase(followTeam.rejected, (state) => {
      if (state?.team) {
        state.team.followersCount -= 1;
        state.team.isFollowing = false;
      }
    });

    builder.addCase(unfollowTeam.rejected, (state) => {
      if (state?.team) {
        state.team.followersCount += 1;
        state.team.isFollowing = true;
      }
    });
  }
});

const { reducer, actions } = lessonSlice;
const { drop } = actions;

export {
  fetchLesson,
  toggleFollowTeam,
  getLessonRating,
  submitLessonFeedback,
  drop as dropLesson
};
export default reducer;
export type { tLessonData };
